// LRPO-5797 — Replace inline autosave that only fired after 30 seconds of total
// inactivity (the previous code reset a timer on every mousemove/scroll/click,
// so it could never fire while someone was actually editing).
//
// User activity must SCHEDULE a save, not postpone one. Each edit restarts the
// debounce timer. A burst of edits collapses to one save.
//
// The MutationObserver is required because the criterion cards (companies,
// demographics) and location pickers insert hidden inputs programmatically via
// createElement — those changes fire no input or change event, so without the
// observer those edits would never trigger a save.
//
// The event names territory:autosaved and territory:autosave-failed are a
// contract relied on by the notice banner (LRPO-5669) and the location pickers'
// server-id write-back (LRPO-5640). Do not rename them or change their detail.

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { delay: { type: Number, default: 3000 } }

  connect() {
    this.pending = false
    this.timer = null
    this.saving = false
    this.dirtyWhileSaving = false

    // LRPO-5864 r5 — NOTHING counts as an edit until a human actually
    // interacts. Page construction fires programmatic change events
    // (pickers initialising) and adds value-carrying inputs (async loads),
    // and both used to look like edits. Until the first TRUSTED
    // pointerdown/keydown anywhere on the page, events and mutations
    // REBASE the baseline snapshot instead of scheduling a save.
    this._userTouched = false
    // LRPO-5898 — save-on-exit: the Exit controller asks for an immediate
    // flush via this document event; the reply is the ordinary
    // territory:autosaved / territory:autosave-failed lifecycle.
    this._onFlushRequest = () => this.flush()
    document.addEventListener("territory:flush-request", this._onFlushRequest)
    this._onFirstTouch = (e) => { if (e && e.isTrusted) this._userTouched = true }
    document.addEventListener("pointerdown", this._onFirstTouch, true)
    document.addEventListener("keydown", this._onFirstTouch, true)

    // Find the form — prefer a descendant, fall back to the known id.
    this.form = this.element.querySelector("form") || document.getElementById("exhibitor-form")
    if (!this.form) return

    // LRPO-5864 — a freshly connected editor has nothing unsaved by definition.
    // The only other reset is `editFormUpdated = false` in a DOMContentLoaded
    // handler in _territory_area.html.erb, and this app runs Turbo Drive, so
    // opening a second territory never re-runs it: the flag arrived true from
    // the territory edited before and Exit warned about changes that were not
    // there. Reset AFTER the form check — with no form this is not the editor
    // and the flag is none of our business.
    if (typeof window !== "undefined") window.editFormUpdated = false

    // LRPO-5864 return 4 — the baseline for "did the DATA change". Opening the
    // page builds DOM inside the form with nobody at the keyboard (operator
    // badges, async <option> loads, suggestion lists); those mutations reach
    // the observer but change no submitted value, so they must not count as
    // edits. Snapshot what a save WOULD post; the observer compares against it.
    this._lastSerialized = this._serializeForm()

    // Bind handlers so they can be removed in disconnect.
    // LRPO-5864 r5 — the event path shares the observer's data-diff gate:
    // before the first real touch everything rebases; after it, an event
    // that leaves the posted data identical is not an edit either.
    this._onFormEdit = () => {
      if (!this._userTouched) {
        this._lastSerialized = this._serializeForm()
        return
      }
      const now = this._serializeForm()
      if (now !== null && this._lastSerialized !== null && now === this._lastSerialized) return
      this.schedule()
    }

    // LRPO-5864 — a manual Save Changes submits the form itself, so there is
    // nothing left to discard. Cancelling the debounce alone left the flag true
    // and Exit warned after a successful manual save.
    this._onSubmit = () => {
      this.cancelPending()
      if (typeof window !== "undefined") window.editFormUpdated = false
    }

    this.form.addEventListener("input", this._onFormEdit)
    this.form.addEventListener("change", this._onFormEdit)
    this.form.addEventListener("submit", this._onSubmit)

    // Watch for programmatic DOM changes (criterion cards, location pickers).
    //
    // ⚠ IGNORE OUR OWN STATUS SURFACES OR THIS IS AN INFINITE LOOP. #autosave-status
    // and #autosave-notice-slot both sit INSIDE this form and are rewritten by the
    // OUTCOME of a save. Left unfiltered, each save mutates the form, which
    // schedules the next save, whose outcome mutates it again: measured at 11 POSTs
    // in 2.4s with a 200ms debounce and nobody at the keyboard. Success loops just
    // as hard as failure — the status label is rewritten either way.
    //
    // ⚠ FILTER, DO NOT DISCONNECT. The observer exists for the criterion cards and
    // the location picker, which add and remove real fields with no input event.
    this.observer = new MutationObserver(records => {
      if (records.every(record => this._isStatusSurface(record.target))) return
      // LRPO-5864 r5 — construction before any human interaction is never
      // an edit, even when it changes the posted data (async loads inject
      // value-carrying inputs): rebase the baseline and stand down.
      if (!this._userTouched) {
        this._lastSerialized = this._serializeForm()
        return
      }
      // LRPO-5864 return 4 — a mutation that leaves the submitted data
      // identical is display work, not an edit. Serialization unknown (null)
      // fails open to the old behaviour.
      const now = this._serializeForm()
      if (now !== null && this._lastSerialized !== null && now === this._lastSerialized) return
      this.schedule()
    })
    this.observer.observe(this.form, { childList: true, subtree: true })
  }

  disconnect() {
    this.cancelPending()
    this.saving = false
    this.dirtyWhileSaving = false
    if (this.form) {
      this.form.removeEventListener("input", this._onFormEdit)
      this.form.removeEventListener("change", this._onFormEdit)
      this.form.removeEventListener("submit", this._onSubmit)
    }
    document.removeEventListener("pointerdown", this._onFirstTouch, true)
    document.removeEventListener("keydown", this._onFirstTouch, true)
    if (this._onFlushRequest) document.removeEventListener("territory:flush-request", this._onFlushRequest)
    this.observer?.disconnect()
  }

  // True when a mutation came from a display-only surface the autosave itself
  // writes, rather than from the form's data. Text nodes report themselves as the
  // target, so climb to an element before asking.
  //
  // ⚠ `[data-autosave-ignore]` is the general rule and new elements should use it —
  // this is the third such surface (status label, error slot, tab counters) and
  // there will be more. The two ids stay listed so the LRPO-5826 fix cannot regress
  // if someone edits the markup and drops the attribute.
  _isStatusSurface(node) {
    if (!node) return false
    const el = node.nodeType === 1 ? node : node.parentElement
    if (!el || typeof el.closest !== 'function') return false
    return !!el.closest('[data-autosave-ignore], #autosave-status, #autosave-notice-slot')
  }

  // What a save would post, as a comparable string. Returns null when the
  // form cannot be serialized, which callers treat as "assume changed".
  _serializeForm() {
    if (!this.form) return null
    try {
      return new URLSearchParams(new FormData(this.form)).toString()
    } catch (e) {
      return null
    }
  }

  schedule() {
    if (typeof window !== "undefined") window.editFormUpdated = true
    this.cancelPending()
    this.timer = setTimeout(() => {
      this.pending = false
      this.save()
    }, this.delayValue)
    this.pending = true
  }

  cancelPending() {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
    this.pending = false
  }

  // LRPO-5898 — fire the pending work NOW (Exit's save-on-exit path).
  // Cancels the debounce and starts a save immediately. If a save is already
  // in flight, do NOT fire a second one (LRPO-5804: a parallel post re-posts
  // criteria whose ids have not been written back and duplicates them) —
  // mark the queued edit instead; save()'s finally() re-schedules it and the
  // caller keeps listening for the lifecycle events.
  flush() {
    this.cancelPending()
    if (this.saving) {
      // Queue a follow-up and mark it IMMEDIATE: finally() below fires it
      // directly instead of re-arming the debounce. Marking dirty even when
      // nothing was pending costs at worst one redundant idempotent post,
      // and guarantees the caller always receives a fresh settled event.
      this.dirtyWhileSaving = true
      this._flushQueued = true
      return
    }
    this.save()
  }

  save() {
    // LRPO-5804 — never two in flight. The form posts wholesale, so a second
    // save started mid-flight re-posts criteria whose server ids have not been
    // written back yet (territory:autosaved does that, LRPO-5640) — Rails then
    // INSERTs a duplicate and idx_tc_unique rejects it.
    //
    // ⚠ REMEMBER the edit rather than dropping it. Skipping outright would
    // silently lose whatever the user typed during the request, which is worse
    // than the 500 this replaces.
    if (this.saving) {
      this.dirtyWhileSaving = true
      return
    }
    this.saving = true

    const formData = new FormData(this.form)

    document.dispatchEvent(new CustomEvent("territory:autosaving", { detail: { source: 'form' } }))

    fetch(this.form.action, {
      method: this.form.method.toUpperCase(),
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
        "Accept": "application/json"
      },
      body: formData
    })
      .then(async response => {
        const data = await response.json().catch(() => ({}))
        if (!response.ok) {
          const failure = new Error(data.message || "Auto-save failed")
          failure.detail = data
          throw failure
        }
        return data
      })
      .then(data => {
        // LRPO-5864 — THE RESPONSE'S OWN WRITE-BACK IS NOT A USER EDIT.
        //
        // Applying a response mutates the form: territory_companies_controller
        // listens for territory:autosaved and writes the server's ids into the
        // hidden inputs, synchronously, inside the dispatch below. Those are
        // childList mutations on elements that are NOT autosave-ignored — and
        // they must not be, because they hold real user data — so the observer
        // sees them and schedule() sets editFormUpdated = true again.
        //
        // The previous two attempts added more places that switch the flag OFF.
        // Neither could work: the re-dirtying happens AFTER the clear, on the
        // same tick. What has to change is that the write-back never counts.
        //
        // ⚠ ORDER IS THE WHOLE FIX. Drain first so a genuine edit made while the
        // request was in flight is not lost, dispatch, then drain again to
        // discard what the response itself caused. MutationObserver callbacks
        // are microtasks, so takeRecords() straight after a synchronous dispatch
        // empties the queue before the callback can run.
        const queued = this.observer ? this.observer.takeRecords() : []
        const userEditedDuringRequest = queued.some(record => !this._isStatusSurface(record.target))

        // LRPO-5898 — tell listeners whether this save SETTLED the form or
        // more work remains (an edit arrived mid-flight, sits in the debounce,
        // or is queued behind this request). Exit's save-on-exit keys off
        // this instead of window.editFormUpdated, which is cleared only
        // AFTER this dispatch returns.
        const settled = !userEditedDuringRequest && !this.pending && !this.dirtyWhileSaving

        document.dispatchEvent(new CustomEvent("territory:autosaved", { detail: { ...data, source: 'form', settled } }))

        // Everything queued by the dispatch above is the response being applied.
        this.observer?.takeRecords()

        // LRPO-5864 return 4 — the snapshot now includes the response's own
        // write-back (ids etc.), applied synchronously by the dispatch above.
        this._lastSerialized = this._serializeForm()

        if (userEditedDuringRequest) {
          // A real edit arrived mid-flight and this response does not cover it.
          this.schedule()
        } else if (typeof window !== "undefined" && !this.pending && !this.dirtyWhileSaving) {
          // ⚠ STILL CHECK pending AND dirtyWhileSaving. An edit can also be
          // sitting in the debounce or queued behind the in-flight guard, where
          // it never reached the observer at all. Clearing here would let Exit
          // walk away from it without a word.
          window.editFormUpdated = false
        }
      })
      .catch(error => {
        console.error("Failed to auto-save:", error)
        document.dispatchEvent(new CustomEvent("territory:autosave-failed", {
          detail: { ...(error.detail || { message: error.message }), source: 'form' }
        }))
      })
      .finally(() => {
        this.saving = false
        if (this.dirtyWhileSaving) {
          this.dirtyWhileSaving = false
          if (this._flushQueued) {
            // LRPO-5898 — an exit-requested flush arrived mid-flight: the
            // follow-up must fire NOW, not after another debounce the
            // exiting user would never wait out. Clear any timer a .then-side
            // schedule() armed in the same window, or it fires a third,
            // redundant save 3s later.
            this._flushQueued = false
            this.cancelPending()
            this.save()
          } else {
            this.schedule()
          }
        }
      })
  }
}
